// Centralne helpery roli — używać WSZĘDZIE zamiast inline checks na localStorage.
// Zmiana wymagań ról = jedna funkcja, nie 50 plików.
//
// Hierarchia (od najwyższego):
//   1. headadmin (= isHead) — może wszystko niezależnie od kontekstu firmy
//   2. superadmin              — może wszystko w obrębie swojej firmy
//   3. admin / manager / itp.  — wg `pagesWithAccess`
//   4. user                    — minimalne uprawnienia
//
// JWT czasem ma `role: 'superadmin'` jako effectiveRole (gdy raw=headadmin), a w localStorage
// trafia oryginalny string. Stąd 3 flagi do sprawdzenia: role / actualRole / isHead.

function get(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

/** Head admin — najwyższa rola, traktujemy go jak super-superadmina (wszystkie firmy). */
export function isHeadAdmin() {
  const role = get('role');
  const actualRole = get('actualRole');
  const isHead = get('isHead') === 'true';
  return role === 'headadmin' || actualRole === 'headadmin' || isHead;
}

/** Super admin — najwyższa rola w obrębie firmy. Head admin też się kwalifikuje. */
export function isSuperAdmin() {
  return isHeadAdmin() || get('role') === 'superadmin';
}

/** Może edytować dane krytyczne firmy — head/super. */
export function canEditCompany() {
  return isSuperAdmin();
}

/** Może cofać/usuwać importy, rozliczenia, wypłaty. */
export function canUndoOperations() {
  // admin też ma to uprawnienie historycznie
  return isSuperAdmin() || get('role') === 'admin';
}

/** Może zarządzać innymi adminami i przyznawać dostępy. */
export function canManageAdmins() {
  return isSuperAdmin();
}

/** Może przypisywać zadania (Tasks). Manager też. */
export function canAssignTasks() {
  return isSuperAdmin();
}

/** Pełen dostęp do wszystkich firm — head admin lub flaga `hasAccessToAllCompanies`. */
export function hasAccessToAllCompanies() {
  return isHeadAdmin() || get('hasAccessToAllCompanies') === 'true';
}

/**
 * Sprawdza czy aktualny user ma dostęp do konkretnej PODZAKŁADKI strony.
 *
 * Konwencja: w `pagesWithAccess` (LocalStorage) wpisy mają format
 * `<page>` lub `<page>.<subtab>`. Dostęp do `<page>` (rodzica) implikuje
 * wszystkie sub-taby. Head/super admin zawsze widzi wszystko.
 *
 * Przykład:
 *   canAccessSubtab('integrations', 'bolt')
 *
 * @param {string} parent — slug strony rodzica, np. 'integrations'
 * @param {string} sub    — slug sub-taba, np. 'bolt'
 * @returns {boolean}
 */
export function canAccessSubtab(parent, sub) {
  if (isSuperAdmin()) return true;
  let pages = [];
  try { pages = JSON.parse(get('pages') || '[]'); } catch { /* ignore */ }
  if (!Array.isArray(pages)) return false;
  return pages.includes(parent) || pages.includes(`${parent}.${sub}`);
}

/**
 * Sprawdza dostęp tylko do podzakładki "edycja" (pole `pagesWithEdit`).
 * Headset/super zawsze może edytować. Dostęp do edycji rodzica implikuje
 * edycję wszystkich sub-tabów. Wymaga że user MA TEŻ podgląd (canAccessSubtab).
 */
export function canEditSubtab(parent, sub) {
  if (!canAccessSubtab(parent, sub)) return false;
  if (isSuperAdmin()) return true;
  let pagesEdit = [];
  try { pagesEdit = JSON.parse(get('pagesWithEdit') || '[]'); } catch { /* ignore */ }
  if (!Array.isArray(pagesEdit)) return false;
  return pagesEdit.includes(parent) || pagesEdit.includes(`${parent}.${sub}`);
}

export default {
  isHeadAdmin,
  isSuperAdmin,
  canEditCompany,
  canUndoOperations,
  canManageAdmins,
  canAssignTasks,
  hasAccessToAllCompanies,
  canAccessSubtab,
  canEditSubtab,
};
